import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHero, PageShell } from "@project/user/components/page-shell";
import { Reveal } from "@project/user/components/motion";
import { useI18n } from "@project/lib/use-i18n";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { publicJainPanchangService } from "@project/services/user/jain-panchang.service";
import type { JainPanchang } from "@project/types/jain-panchang";
import { PanchangCalendar } from "./panchang-calendar";
import { PanchangDetailDialog } from "./panchang-detail-dialog";
import { PanchangTodayHighlight } from "./panchang-today-highlight";
import { PanchangUpcomingList } from "./panchang-upcoming-list";

export default function JainPanchangPage() {
  const { t } = useI18n();
  const [month, setMonth] = useState(() => new Date());
  const [selectedEntry, setSelectedEntry] = useState<JainPanchang | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: highlights, isLoading: highlightsLoading } = useQuery({
    queryKey: ["public-jain-panchang-highlights"],
    queryFn: () => publicJainPanchangService.listHighlights(),
  });

  const { data: entries = [], isLoading } = useQuery({
    queryKey: [
      "public-jain-panchang",
      month.getFullYear(),
      month.getMonth() + 1,
    ],
    queryFn: () =>
      publicJainPanchangService.listForMonth(
        month.getFullYear(),
        month.getMonth() + 1
      ),
  });

  const openDetails = (entry: JainPanchang) => {
    setSelectedEntry(entry);
    setDialogOpen(true);
  };

  const todayEntry = highlights?.today ?? null;
  const upcomingEntries = highlights?.upcoming ?? [];

  return (
    <PageShell>
      <PageHero
        eyebrow={t("community.jainPanchang.hero.eyebrow")}
        title={t("community.jainPanchang.hero.title")}
        subtitle={t("community.jainPanchang.hero.subtitle")}
        image={HERO_IMAGES.jainPanchang}
      />

      <section className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:py-14">
        {highlightsLoading ? (
          <div className="rounded-3xl border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
            {t("community.jainPanchang.highlights.loading")}
          </div>
        ) : todayEntry ? (
          <Reveal variant="morph">
            <div className="anim-fade-up" style={{ animationDelay: "200ms" }}>
              <PanchangTodayHighlight
                entry={todayEntry}
                onViewDetails={openDetails}
                labels={{
                  eyebrow: t("community.jainPanchang.today.eyebrow"),
                  jainMonth: t("community.jainPanchang.today.jainMonth"),
                  paksha: t("community.jainPanchang.today.paksha"),
                  currentTithi: t("community.jainPanchang.today.currentTithi"),
                  significance: t("community.jainPanchang.today.significance"),
                  viewDetails: t("community.jainPanchang.today.viewDetails"),
                  donateToday: t("community.jainPanchang.today.donateToday"),
                }}
              />
            </div>
          </Reveal>
        ) : null}

        <Reveal variant="morph" delay={120}>
          <div className="anim-fade-up" style={{ animationDelay: "340ms" }}>
            <PanchangCalendar
              month={month}
              onMonthChange={setMonth}
              entries={entries}
              loading={isLoading}
              onSelectEntry={openDetails}
              labels={{
                today: t("community.jainPanchang.calendar.today"),
                weekdays: t("community.jainPanchang.calendar.weekdays")
                  .split(",")
                  .map(day => day.trim()),
                loading: t("community.jainPanchang.calendar.loading"),
                vow: t("community.jainPanchang.calendar.vow"),
              }}
            />
          </div>
        </Reveal>

        {!highlightsLoading && upcomingEntries.length > 0 ? (
          <Reveal variant="fade" delay={80}>
            <PanchangUpcomingList
              entries={upcomingEntries}
              onSelectEntry={openDetails}
              labels={{
                title: t("community.jainPanchang.upcoming.title"),
                details: t("community.jainPanchang.upcoming.details"),
                donate: t("community.jainPanchang.upcoming.donate"),
              }}
            />
          </Reveal>
        ) : null}
        {!isLoading && entries.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">
            {t("community.jainPanchang.empty")}
          </p>
        ) : null}
      </section>

      <PanchangDetailDialog
        entry={selectedEntry}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        labels={{
          subtitle: t("community.jainPanchang.modal.subtitle"),
          lunarCalendar: t("community.jainPanchang.modal.lunarCalendar"),
          monthPaksha: t("community.jainPanchang.modal.monthPaksha"),
          significance: t("community.jainPanchang.modal.significance"),
          recommended: t("community.jainPanchang.modal.recommended"),
          goBack: t("community.jainPanchang.modal.goBack"),
          donate: t("community.jainPanchang.modal.donate"),
        }}
      />
    </PageShell>
  );
}
